import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

export type ThemeMode = 'dark' | 'light' | 'auto';
export type ResolvedTheme = 'dark' | 'light';
export type FontSize = 'small' | 'medium' | 'large';
export type Layout = 'app' | 'panel';

export interface ThemeContextValue {
  theme: ResolvedTheme;
  fontSize: FontSize;
  layout: Layout;
  /** Where portals (dialogs, drawers) mount; they get their own `.nyk-ui-root` with the same attributes. */
  portalContainer?: HTMLElement;
}

const ThemeContext = createContext<ThemeContextValue>({ theme: 'dark', fontSize: 'medium', layout: 'app' });

export const useTheme = (): ThemeContextValue => useContext(ThemeContext);

function usePrefersDark(): boolean {
  const [dark, setDark] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : true
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setDark(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return dark;
}

export const FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap';

/** Opt-in: inject the Poppins stylesheet once (standalone apps link it in index.html instead). */
export function loadFonts(): void {
  if (typeof document === 'undefined' || document.querySelector('link[data-nb-fonts]')) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = FONTS_HREF;
  link.setAttribute('data-nb-fonts', '');
  document.head.appendChild(link);
}

export interface ThemeRootProps {
  theme?: ThemeMode;
  fontSize?: FontSize;
  layout?: Layout;
  portalContainer?: HTMLElement;
  loadFonts?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

/** `.nyk-ui-root` with `data-theme` (auto resolved to dark|light), `data-font-size` and `data-layout`; scoped tokens + reset. */
export function ThemeRoot({
  theme = 'dark',
  fontSize = 'medium',
  layout = 'app',
  portalContainer,
  loadFonts: fonts,
  className,
  style,
  children,
}: ThemeRootProps) {
  const prefersDark = usePrefersDark();
  const resolved: ResolvedTheme = theme === 'auto' ? (prefersDark ? 'dark' : 'light') : theme;
  useEffect(() => {
    if (fonts) loadFonts();
  }, [fonts]);
  const value = useMemo<ThemeContextValue>(
    () => ({ theme: resolved, fontSize, layout, portalContainer }),
    [resolved, fontSize, layout, portalContainer]
  );
  return (
    <ThemeContext.Provider value={value}>
      <div
        className={['nyk-ui-root', className].filter(Boolean).join(' ')}
        data-theme={resolved}
        data-font-size={fontSize === 'medium' ? undefined : fontSize}
        data-layout={layout}
        style={style}
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

/** Attributes for a portal root that mirrors the nearest ThemeRoot. */
export function useRootAttributes(): Record<string, string | undefined> {
  const t = useTheme();
  return {
    className: 'nyk-ui-root nyk-ui-portal',
    'data-theme': t.theme,
    'data-font-size': t.fontSize === 'medium' ? undefined : t.fontSize,
    'data-layout': t.layout,
  };
}
